import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Send, Wrench, Loader2, Compass, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const NAVY = "#1a2a6c";
const TEAL = "#00b09b";
const COMPASS_KEYS = [
  { k: "message", label: "Message" },
  { k: "audience", label: "Audience" },
  { k: "offer", label: "Offer" },
  { k: "traffic", label: "Traffic" },
  { k: "conversion", label: "Conversion" },
];

function CompassStrip({ compass }) {
  if (!compass) return null;
  return (
    <div className="grid grid-cols-5 gap-2 border-b border-border bg-white px-4 py-3" data-testid="milo-compass">
      {COMPASS_KEYS.map(({ k, label }) => {
        const v = compass[k];
        const known = v !== null && v !== undefined;
        return (
          <div key={k} className="text-center" data-testid={`milo-compass-${k}`}>
            <div className="mx-auto mb-1 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full transition-all"
                style={{ width: known ? `${v}%` : "0%", background: TEAL }} />
            </div>
            <p className="text-[10px] font-semibold" style={{ color: NAVY }}>{label}</p>
            <p className="text-[10px] text-muted-foreground">{known ? v : "—"}</p>
          </div>
        );
      })}
    </div>
  );
}

function MoveCard({ payload, onTool, onReport, reporting, reportText, setReportText, onSubmitReport, busy }) {
  const Row = ({ emoji, label, children }) => (
    <div className="mb-3" data-testid={`milo-move-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <p className="mb-0.5 text-[11px] font-bold uppercase tracking-wide" style={{ color: TEAL }}>
        {emoji} {label}
      </p>
      <p className="text-sm leading-relaxed text-[#1C1917]">{children}</p>
    </div>
  );
  return (
    <div className="rounded-2xl border border-border bg-white p-4 shadow-sm" data-testid="milo-move-card">
      <div className="mb-3 inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white"
        style={{ background: NAVY }}>
        Your Next Best Move™
      </div>
      <Row emoji="🔎" label="What I'm seeing">{payload.seeing}</Row>
      <Row emoji="🎯" label="What matters most"><span className="font-semibold capitalize">{payload.bottleneck}</span></Row>
      <Row emoji="🧭" label="Your next best move">{payload.next_move}</Row>
      <Row emoji="💡" label="Why this move">{payload.why}</Row>
      <Row emoji="📊" label="What we're looking for">{payload.looking_for}</Row>
      <Button onClick={() => onTool(payload.route)} data-testid="milo-lets-do-it"
        className="mt-1 w-full rounded-full py-5 text-sm font-semibold text-white hover:opacity-90"
        style={{ background: NAVY }}>
        <Wrench size={16} className="mr-1.5" /> 🛠️ Let's do it — {payload.cta_label}
      </Button>
      {!reporting ? (
        <button onClick={() => onReport(payload.move_id)} data-testid="milo-report-toggle"
          className="mt-2.5 w-full text-center text-xs font-medium text-muted-foreground hover:underline">
          I've done this — tell MILO what happened
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <Textarea value={reportText} onChange={(e) => setReportText(e.target.value)} rows={3}
            data-testid="milo-report-text" placeholder="What happened when you tried this?" className="text-sm" />
          <Button onClick={onSubmitReport} disabled={busy || !reportText.trim()} data-testid="milo-report-submit"
            className="w-full rounded-full text-sm font-semibold text-white hover:opacity-90" style={{ background: TEAL }}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : "Send to MILO"}
          </Button>
        </div>
      )}
    </div>
  );
}

function QuestionsBlock({ payload, disabled, onSubmit, busy }) {
  const [vals, setVals] = useState({});
  const allFilled = (payload.fields || []).every((f) => (vals[f.key] || "").trim());
  return (
    <div className="rounded-2xl border border-border bg-white p-4 shadow-sm" data-testid="milo-questions">
      <p className="mb-3 text-sm leading-relaxed text-[#1C1917]">{payload.message}</p>
      <div className="space-y-3">
        {(payload.fields || []).map((f) => (
          <div key={f.key} className="space-y-1">
            <label className="text-xs font-semibold" style={{ color: NAVY }}>{f.label}</label>
            <Input disabled={disabled} value={vals[f.key] || ""} placeholder={f.placeholder}
              data-testid={`milo-q-${f.key}`}
              onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))} className="text-sm" />
          </div>
        ))}
      </div>
      {!disabled && (
        <Button onClick={() => onSubmit(vals)} disabled={busy || !allFilled} data-testid="milo-answers-submit"
          className="mt-3 w-full rounded-full text-sm font-semibold text-white hover:opacity-90" style={{ background: NAVY }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Show my Next Best Move"}
        </Button>
      )}
    </div>
  );
}

export default function MiloPanel({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [compass, setCompass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [reportFor, setReportFor] = useState(null);
  const [reportText, setReportText] = useState("");
  const scrollRef = useRef(null);
  const bootedRef = useRef(false);

  const scrollDown = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const refreshCompass = useCallback(async () => {
    try { const { data } = await api.get("/milo"); setCompass(data.compass || null); } catch (e) { /* noop */ }
  }, []);

  const post = useCallback(async (body, optimisticUser) => {
    if (optimisticUser) setMessages((m) => [...m, optimisticUser]);
    setLoading(true);
    scrollDown();
    try {
      const { data } = await api.post("/milo/message", body);
      setMessages((m) => [...m, data.reply]);
      refreshCompass();
      scrollDown();
    } catch (e) {
      toast.error("MILO hit a snag. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [refreshCompass]);

  useEffect(() => {
    if (!open || bootedRef.current) return;
    bootedRef.current = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/milo");
        setMessages(data.messages || []);
        setCompass(data.compass || null);
        if (!data.messages || data.messages.length === 0) {
          await post({});
        }
      } catch (e) {
        toast.error("Couldn't reach MILO. Please try again.");
      } finally {
        setLoading(false);
        scrollDown();
      }
    })();
  }, [open, post]);

  const submitText = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    post({ message: text }, { id: `local-${Date.now()}`, role: "user", kind: "text", content: text });
  };

  const submitAnswers = (vals) => {
    const summary = Object.entries(vals).filter(([, v]) => (v || "").trim())
      .map(([, v]) => v.trim()).join(" · ");
    post({ answers: vals }, { id: `local-${Date.now()}`, role: "user", kind: "text", content: summary });
  };

  const submitReport = () => {
    const text = reportText.trim();
    if (!text) return;
    const moveId = reportFor;
    setReportFor(null);
    setReportText("");
    post({ message: text, completed_move_id: moveId },
      { id: `local-${Date.now()}`, role: "user", kind: "text", content: text });
  };

  const goTool = (route) => { onOpenChange(false); navigate(route); };

  const doReset = async () => {
    try {
      await api.post("/milo/reset");
      setMessages([]); setCompass(null); setReportFor(null);
      await post({});
    } catch (e) { toast.error("Couldn't restart MILO."); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="milo-panel">
        <SheetTitle className="sr-only">MILO — Marketing Strategist</SheetTitle>
        <SheetDescription className="sr-only">Chat with MILO to get your Next Best Move based on your project data.</SheetDescription>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 text-white" style={{ background: NAVY }}>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: TEAL }}>
              <Compass size={18} />
            </div>
            <div>
              <p className="font-head text-base font-bold leading-none">MILO</p>
              <p className="mt-0.5 text-[11px] text-white/70">Your Next Best Move™</p>
            </div>
          </div>
          <button onClick={doReset} data-testid="milo-reset" title="Start fresh"
            className="flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-medium text-white/80 hover:bg-white/10">
            <RefreshCw size={13} /> Restart
          </button>
        </div>

        <CompassStrip compass={compass} />

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-[#FAFAF9] px-4 py-4" data-testid="milo-messages">
          {messages.map((m, i) => {
            const isLast = i === messages.length - 1;
            if (m.role === "user") {
              return (
                <div key={m.id} className="flex justify-end" data-testid="milo-user-msg">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2 text-sm text-white" style={{ background: NAVY }}>
                    {m.content}
                  </div>
                </div>
              );
            }
            if (m.kind === "questions") {
              return (
                <div key={m.id} className="max-w-[92%]">
                  <QuestionsBlock payload={m.payload} disabled={!isLast} onSubmit={submitAnswers} busy={loading} />
                </div>
              );
            }
            if (m.kind === "move") {
              return (
                <div key={m.id} className="max-w-[95%]">
                  {m.content && <p className="mb-2 px-1 text-sm font-medium" style={{ color: NAVY }}>{m.content}</p>}
                  <MoveCard payload={m.payload} onTool={goTool}
                    onReport={(id) => { setReportFor(id); setReportText(""); }}
                    reporting={reportFor === m.payload.move_id} reportText={reportText}
                    setReportText={setReportText} onSubmitReport={submitReport} busy={loading} />
                </div>
              );
            }
            return (
              <div key={m.id} className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-border bg-white px-3.5 py-2 text-sm text-[#1C1917]">
                  {m.content}
                </div>
              </div>
            );
          })}
          {loading && (
            <div className="flex items-center gap-2 px-1 text-sm text-muted-foreground" data-testid="milo-thinking">
              <Loader2 size={15} className="animate-spin" style={{ color: TEAL }} /> MILO is thinking…
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="flex items-end gap-2 border-t border-border bg-white p-3">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1}
            data-testid="milo-input" placeholder="Ask MILO anything, or tell it what happened…"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitText(); } }}
            className="max-h-28 min-h-[42px] flex-1 resize-none text-sm" />
          <Button onClick={submitText} disabled={loading || !input.trim()} data-testid="milo-send"
            className="h-[42px] w-[42px] shrink-0 rounded-xl p-0 text-white hover:opacity-90" style={{ background: TEAL }}>
            <Send size={17} />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
